import { motion } from 'framer-motion';
import contactimage1 from "../assets/images/contactimage1.avif";
import { useState } from 'react';
import sendEmail from '../services/mail/mailject';
import { useNavigate } from 'react-router-dom';
export default function Contact() {

    // Smooth scrolling setup
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const result = await sendEmail(
                formData.email,
                formData.name,
                formData.message,
                'New Contact Form Submission from GMNexus'
            );

            if (result.success) {
                setSubmitStatus({ type: 'success', message: 'Message sent successfully! We\'ll get back to you soon.' });
                setFormData({ name: '', email: '', message: '' }); // Reset form
            } else {
                setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again or contact us directly.' });
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus({ type: 'error', message: 'An unexpected error occurred. Please try again later.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Animation variants
    const container = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                when: "beforeChildren"
            }
        }
    };

    const item = {
        hidden: { y: 40, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 10
            }
        }
    };

    const heroVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.8, ease: "easeOut" }
        }
    };

    const heroTextVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { delay: 0.3, duration: 0.8 }
        }
    };

    const slideInLeft = {
        hidden: { x: -40, opacity: 0 },
        visible: {
            x: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 80,
                damping: 10
            }
        }
    };

    const slideInRight = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 80,
                damping: 10
            }
        }
    };

    const cardHover = {
        scale: 1.02,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        transition: { duration: 0.3 }
    };

    const buttonHover = {
        scale: 1.03,
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        transition: { duration: 0.2 }
    };

    const buttonTap = {
        scale: 0.98,
        transition: { duration: 0.1 }
    };

    const floatingAnimation = {
        animate: {
            y: [0, -15, 0],
            transition: {
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
            }
        }
    };

    const pulseAnimation = {
        animate: {
            scale: [1, 1.05, 1],
            transition: {
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
            }
        }
    };

    return (
        <div className="bg-white">
            {/* 4K Hero Image Section */}
            <motion.div
                initial="hidden"
                animate="visible"
                variants={heroVariants}
                className="relative w-full h-screen max-h-[80vh] overflow-hidden"
            >
                <img
                    src={contactimage1}
                    alt="Professional Support Team"
                    className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <motion.div
                        variants={heroTextVariants}
                        className="text-center px-4"
                    >
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">
                            Get In <span className="text-yellow-400">Touch</span>
                        </h1>
                        <p className="text-xl text-white/90 max-w-2xl mx-auto">
                            We're here to help and answer any questions you might have
                        </p>
                        <motion.div
                            variants={floatingAnimation}
                            animate="animate"
                            className="mt-12"
                        >
                            <svg className="w-12 h-12 mx-auto text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Stats Section */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={container}
                className="bg-black text-white py-16 px-4"
            >
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
                    {[
                        { number: '24/7', text: 'Support Availability' },
                        { number: '10+', text: 'Projects Delivered' },
                        { number: '98%', text: 'Customer Satisfaction' },
                        { number: '30min', text: 'Avg. Response Time' }
                    ].map((stat, index) => (
                        <motion.div
                            key={index}
                            variants={item}
                            whileHover={cardHover}
                            className="text-center p-6 bg-gray-900 rounded-xl"
                        >
                            <motion.h3
                                variants={pulseAnimation}
                                animate="animate"
                                className="text-4xl font-bold text-yellow-400 mb-2"
                            >
                                {stat.number}
                            </motion.h3>
                            <p className="text-gray-300">{stat.text}</p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Contact Content */}
            <motion.div
                initial="hidden"
                animate="visible"
                variants={container}
                className="text-gray-800 px-4 py-16 md:px-20 max-w-7xl mx-auto"
                id='shedulecall'
            >
                {/* Header */}
                <motion.div
                    variants={item}
                    className="text-center mb-16"
                >
                    <motion.h1
                        variants={item}
                        className="text-4xl md:text-5xl font-bold mb-4"
                    >
                        Contact <span className="text-yellow-500">Us</span>
                    </motion.h1>
                    <motion.p
                        variants={item}
                        className="text-lg text-gray-600 max-w-3xl mx-auto"
                    >
                        We'd love to hear from you! Whether you have a question about our services, pricing, or anything else, our team is ready to answer all your questions.
                    </motion.p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" >
                    {/* Contact Form */}
                    <motion.div variants={slideInLeft} className="lg:pr-8">
                        <motion.h2
                            variants={item}
                            className="text-2xl font-bold mb-6 text-black"
                        >
                            Send us a message
                        </motion.h2>
                        <motion.form
                            onSubmit={handleSubmit}
                            whileHover={{
                                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
                            }}
                            className="space-y-6 bg-white p-8 rounded-2xl shadow-lg border border-gray-100"
                        >
                            <motion.div variants={item}>
                                <label className="block text-sm font-semibold mb-2 text-gray-700">Full Name</label>
                                <motion.input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    whileFocus={{
                                        boxShadow: "0 0 0 2px rgba(234, 179, 8, 0.5)",
                                        scale: 1.01
                                    }}
                                    type="text"
                                    className="w-full p-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                    placeholder="Your Name"
                                    required
                                />
                            </motion.div>
                            <motion.div variants={item}>
                                <label className="block text-sm font-semibold mb-2 text-gray-700">Email Address</label>
                                <motion.input
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    whileFocus={{
                                        boxShadow: "0 0 0 2px rgba(234, 179, 8, 0.5)",
                                        scale: 1.01
                                    }}
                                    type="email"
                                    className="w-full p-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                    placeholder="you@example.com"
                                    required
                                />
                            </motion.div>
                            <motion.div variants={item}>
                                <label className="block text-sm font-semibold mb-2 text-gray-700">Your Message</label>
                                <motion.textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    whileFocus={{
                                        boxShadow: "0 0 0 2px rgba(234, 179, 8, 0.5)",
                                        scale: 1.01
                                    }}
                                    rows={5}
                                    className="w-full p-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                    placeholder="How can we help you?"
                                    required
                                />
                            </motion.div>
                            {/* Status Message */}
                            {submitStatus && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`p-4 rounded-xl text-center ${submitStatus.type === 'success'
                                        ? 'bg-green-100 text-green-700 border border-green-200'
                                        : 'bg-red-100 text-red-700 border border-red-200'
                                        }`}
                                >
                                    {submitStatus.message}
                                </motion.div>
                            )}

                            <motion.button
                                variants={item}
                                whileHover={!isSubmitting ? buttonHover : {}}
                                whileTap={!isSubmitting ? buttonTap : {}}
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full px-6 py-4 rounded-xl font-bold text-lg transition ${isSubmitting
                                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                    : 'bg-yellow-500 text-black hover:bg-yellow-600'
                                    }`}
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </div>
                                ) : (
                                    'Send Message'
                                )}
                            </motion.button>
                        </motion.form>
                    </motion.div>

                    {/* Contact Info */}
                    <motion.div
                        variants={slideInRight}
                        className="lg:pl-8"
                    >
                        <motion.h2
                            variants={item}
                            className="text-2xl font-bold mb-6 text-black"
                        >
                            Get in touch
                        </motion.h2>

                        <motion.div
                            variants={container}
                            className="space-y-8 mb-12"
                        >
                            {[
                                {
                                    icon: (
                                        <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    ),
                                    title: "Email",
                                    content: "geocodesoftsolution@gmail.com",
                                    link: "mailto:geocodesoftsolution@gmail.com"
                                },
                                {
                                    icon: (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-8 w-8 mr-2 mt-0.5 text-yellow-500"
                                            viewBox="0 0 32 32"
                                            fill="currentColor"
                                        >
                                            <path d="M16.003 3C9.376 3 3.999 8.377 3.999 15.004c0 2.654.875 5.116 2.355 7.126L5 29l7.074-1.339a12.94 12.94 0 0 0 3.93.603c6.627 0 12.004-5.377 12.004-12.004S22.63 3 16.003 3zm0 21.89c-1.302 0-2.57-.34-3.692-.985l-.264-.156-4.198.794.804-4.097-.171-.27a8.976 8.976 0 0 1-1.384-4.772c0-4.962 4.043-9.005 9.005-9.005s9.005 4.043 9.005 9.005-4.043 9.005-9.005 9.005zm5.232-6.755c-.285-.143-1.695-.84-1.958-.934-.263-.095-.454-.143-.646.143-.192.285-.742.934-.91 1.126-.167.191-.335.215-.62.072-.285-.143-1.202-.443-2.29-1.414-.846-.754-1.418-1.687-1.585-1.972-.167-.285-.018-.439.126-.582.129-.128.285-.335.428-.503.143-.167.19-.287.286-.478.095-.191.048-.358-.024-.503-.072-.143-.646-1.559-.885-2.132-.23-.553-.465-.477-.646-.486l-.552-.01c-.191 0-.503.072-.767.358s-1.007.983-1.007 2.397 1.031 2.776 1.175 2.965c.143.191 2.03 3.098 4.925 4.344.688.297 1.223.475 1.64.61.688.219 1.314.188 1.81.114.552-.082 1.695-.693 1.934-1.364.239-.67.239-1.246.167-1.364-.071-.119-.262-.191-.547-.334z" />
                                        </svg>
                                    ),
                                    title: "Whatsapp",
                                    content: "+91 9025703761",
                                    link: "tel:+91 9025703761"
                                },
                                {
                                    icon: (
                                        <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    ),
                                    title: "Address",
                                    content: "Kurumpanai, Marthandam, Tamil Nadu, India",
                                    link: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7898.21954276736!2d77.22278234405219!3d8.191696186758469!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b04fdd774ddf995%3A0xbf33ef47e53ccec4!2sKurumpanai%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1758524857601!5m2!1sen!2sin"
                                }].map((contact, idx) => (
                                    <motion.a
                                        key={idx}
                                        href={contact.link}
                                        variants={item}
                                        whileHover={{ x: 10, transition: { duration: 0.2 } }}
                                        className="flex items-start p-4 rounded-xl bg-gray-50 hover:bg-yellow-50 transition group"
                                    >
                                        <motion.div
                                            whileHover={{ rotate: 10 }}
                                            className="mr-4"
                                        >
                                            {contact.icon}
                                        </motion.div>
                                        <div>
                                            <h3 className="font-semibold text-lg text-black group-hover:text-yellow-600 transition">{contact.title}</h3>
                                            <p className="text-gray-600">{contact.content}</p>
                                        </div>
                                    </motion.a>
                                ))}
                        </motion.div>

                        {/* Social Media */}
                        <motion.div
                            variants={item}
                            className="mb-12"
                        >
                            <h3 className="text-xl font-semibold mb-4 text-black">Follow Us</h3>
                            <div className="flex space-x-4">
                                {[
                                    { platform: 'facebook', url: 'https://www.facebook.com/yourpage' },
                                    { platform: 'twitter', url: 'https://x.com/Geocode_India/'},
                                    { platform: 'instagram', url: 'https://www.instagram.com/geocode_softsolution/' },
                                    { platform: 'linkedin', url: 'https://www.linkedin.com/company/geocodesoftsolution' },
                                ].map((item, idx) => (
                                    <motion.a
                                        key={idx}
                                        href={item.url}
                                        target="_blank" // Opens in new tab
                                        rel="noopener noreferrer" // Security best practice
                                        whileHover={{ y: -5, scale: 1.1, color: "#eab308" }}
                                        whileTap={{ scale: 0.95 }}
                                        className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-yellow-100 transition"
                                    >
                                        <svg
                                            className="w-6 h-6"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d={
                                                    item.platform === 'facebook'
                                                        ? "M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96C18.34 21.21 22 17.06 22 12.06C22 6.53 17.5 2.04 12 2.04Z"
                                                        : item.platform === 'twitter'
                                                            ? "M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"
                                                            : item.platform === 'instagram'
                                                                ? "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
                                                                : "M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                                                }
                                            />
                                        </svg>
                                    </motion.a>
                                ))}
                            </div>
                        </motion.div>

                        {/* Google Map */}
                        <motion.div
                            variants={item}
                            className="overflow-hidden rounded-2xl shadow-lg border-2 border-gray-200"
                        >
                            <motion.iframe
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                title="Company Location"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7898.21954276736!2d77.22278234405219!3d8.191696186758469!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b04fdd774ddf995%3A0xbf33ef47e53ccec4!2sKurumpanai%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1758524857601!5m2!1sen!2sin"
                                className="w-full h-64"
                                loading="lazy"
                                allowFullScreen
                            />
                        </motion.div>
                    </motion.div>
                </div>

                {/* FAQ Section */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={container}
                    className="mt-24"
                >
                    <motion.h2
                        variants={item}
                        className="text-3xl font-bold text-center mb-12 text-black"
                    >
                        Frequently Asked <span className="text-yellow-500">Questions</span>
                    </motion.h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                            {
                                question: "How quickly do you respond to inquiries?",
                                answer: "We typically respond to all inquiries within 24 hours during business days. For urgent matters, please call our support line."
                            },
                            {
                                question: "Do you offer custom solutions?",
                                answer: "Yes, we specialize in creating tailored solutions to meet your specific business needs. Contact us to discuss your requirements."
                            },
                            {
                                question: "What are your business hours?",
                                answer: "Our support team is available 24/7. Our office hours are Monday-Friday, 9AM-6PM IST."
                            },
                            {
                                question: "How can I get a quote for your services?",
                                answer: "You can request a quote by filling out our contact form or scheduling a consultation with one of our experts."
                            }
                        ].map((faq, idx) => (
                            <motion.div
                                key={idx}
                                variants={item}
                                whileHover={cardHover}
                                className="bg-gray-50 p-6 rounded-2xl border border-gray-200"
                            >
                                <h3 className="text-lg font-semibold mb-2 text-black flex items-center">
                                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                                    {faq.question}
                                </h3>
                                <p className="text-gray-600">{faq.answer}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </motion.div>

            {/* CTA Section */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={container}
                className="py-16 px-4 bg-black text-white"
            >
                <div className="max-w-4xl mx-auto text-center">
                    <motion.h2
                        variants={item}
                        className="text-3xl md:text-4xl font-bold mb-6"
                    >
                        Ready to <span className="text-yellow-400">transform</span> your business?
                    </motion.h2>
                    <motion.p
                        variants={item}
                        className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto"
                    >
                        Let's discuss how our solutions can help you achieve your goals.
                    </motion.p>
                    <motion.div
                        variants={item}
                        className="flex flex-col sm:flex-row justify-center gap-4"
                    >
                        <motion.button
                            whileHover={{ ...buttonHover, backgroundColor: "#eab308", color: "#000" }}
                            whileTap={buttonTap}
                            onClick={() => scrollToSection("shedulecall")}
                            className="bg-yellow-500 text-black px-8 py-4 rounded-xl font-bold text-lg"
                        >
                            Schedule a Call
                        </motion.button>
                        <motion.button
                            whileHover={{ ...buttonHover, backgroundColor: "transparent", borderColor: "#eab308" }}
                            whileTap={buttonTap}
                            onClick={() => navigate("/service")}
                            className="border-2 border-white px-8 py-4 rounded-xl font-bold text-lg"
                        >
                            View Our Services
                        </motion.button>
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
}